import Loading from '@/components/Loading';
import Button from '@/components/UI/Button';
import Chip from '@/components/UI/Chip/Chip';
import Switch from '@/components/UI/FormElements/Switch/Switch';
import { errorToast, successToast } from '@/helpers.tsx';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash.ts';
import InVivoError from '@/model/InVivoError.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { ExceptionHandler } from '@/utils/ExceptionHandler.ts';
import type { components } from '@/generated/internal-api/openapi-types';
import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { FC, useState } from 'react';
import { Controller, useForm, useWatch, Validate } from 'react-hook-form@latest';
import Header from '@/components/UI/Header';

type TeamTickatlabDetailsV1 = components['schemas']['TeamTickatlabDetailsV1.schema'];
type TeamTickatlabDetailsUpdateV1 = components['schemas']['TeamTickatlabDetailsUpdateV1.schema'];
type TickatlabEndpointV1 = components['schemas']['TickatlabEndpointV1.schema'];

const INSTANCE_SUGGESTIONS = ['PROD', 'TEST', 'DEV'];

interface TickatlabFormValues {
  url: string;
  header: string;
  instance: string;
  endpoint_overrides: Record<string, string>;
}

const urlValidator: Validate<string, TickatlabFormValues> = (value) => {
  try {
    const url = new URL(value);
    if (url.protocol !== 'https:') {
      return 'Base URL must start with https://';
    }
    return true;
  } catch {
    return 'Invalid Base URL';
  }
};

export const TickatlabConfig: FC = () => {
  const {
    response: detailsResponse,
    loading: detailsLoading,
    invoke: refetchDetails,
  } = useApiHook({
    endpoint: 'GET /api/v1/team/tickatlab-details',
  });

  if (detailsLoading) {
    return <Loading txt="Loading tick@lab Configuration" />;
  }

  if (_isNil(detailsResponse) || detailsResponse.type === 'error') {
    return (
      <div className="pb3" data-test-component="TickatlabConfig" data-test-element="error">
        <Header mainHeaderText="tick@lab Config" />
        <div className="flex flex-row pt4">
          <div className="ui-card pa4 w-50">
            <p className="red">Failed to load tick@lab configuration. Please try refreshing the page.</p>
          </div>
        </div>
      </div>
    );
  }

  return <TickatlabForm details={detailsResponse?.body ?? null} refetchDetails={refetchDetails} />;
};

interface TickatlabFormProps {
  details: TeamTickatlabDetailsV1 | null;
  refetchDetails: () => Promise<unknown>;
}

const TickatlabForm: FC<TickatlabFormProps> = ({ details, refetchDetails }) => {
  const { invoke: updateTickatlab } = useApiHook({
    endpoint: 'PATCH /api/v1/team/tickatlab-details',
    invokeOnInit: false,
    options: { onError: { toast: false, throw: true, capture: false } },
  });

  const existingOverrides = buildExistingOverrides(details);

  const formMethods = useForm<TickatlabFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: {
      url: details?.url ?? '',
      header: '',
      instance: details?.instance ?? 'TEST',
      endpoint_overrides: existingOverrides,
    },
  });

  const { isSubmitting, isSubmitSuccessful } = formMethods.formState;
  const isUpdate = _notNil(details?.url) || isSubmitSuccessful;

  const handleSubmit = async (formData: TickatlabFormValues) => {
    try {
      const hasOverrides = _isNotEmpty(Object.keys(formData.endpoint_overrides));
      const body: TeamTickatlabDetailsUpdateV1 = {
        header: formData.header,
        url: formData.url,
        instance: formData.instance,
        endpoint_overrides: hasOverrides ? formData.endpoint_overrides : undefined,
      };
      await updateTickatlab({ body });
      await refetchDetails();
      successToast('Saved');
    } catch (cause) {
      errorToast('Could not save tick@lab config');
      ExceptionHandler.captureException(
        new InVivoError('Could not save tick@lab details', {
          cause,
          slug: 'tickatlab-details',
          context: { url: formData.url },
        })
      );
    }
  };

  return (
    <div className="pb3" data-test-component="TickatlabForm" data-test-element="container">
      <Header mainHeaderText={`${_isNil(details?.url) ? 'Add' : 'Update'} tick@lab Config`} />
      <div className="flex flex-row pt4">
        <div className="ui-card pa4 flex flex-column w-50">
          <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
            <Controller
              control={formMethods.control}
              name="url"
              rules={{ validate: urlValidator }}
              render={({ field, fieldState, formState }) => (
                <div className="mb3" data-test-element="url-container">
                  <label>Base URL</label>
                  <input
                    {...field}
                    type="url"
                    className={cn({ ui__error: fieldState.invalid })}
                    data-test-element="url-input"
                    placeholder="https://example-dm.atune.com"
                  />
                  <ErrorMessage
                    name="url"
                    errors={formState.errors}
                    render={({ message }) => (
                      <p className="f6 red db" data-test-element="url-error">
                        {message}
                      </p>
                    )}
                  />
                </div>
              )}
            />
            <Controller
              control={formMethods.control}
              name="header"
              rules={{ required: 'Auth Header required' }}
              render={({ field, fieldState, formState }) => (
                <div className="mb3" data-test-element="header-container">
                  <label>Auth Header</label>
                  <input
                    {...field}
                    type="password"
                    placeholder={_isNil(details?.url) ? '' : '✳︎'.repeat(40)}
                    className={cn({ ui__error: fieldState.invalid })}
                    data-test-element="header-input"
                  />
                  <ErrorMessage
                    name="header"
                    errors={formState.errors}
                    render={({ message }) => (
                      <p className="f6 red db" data-test-element="header-error">
                        {message}
                      </p>
                    )}
                  />
                </div>
              )}
            />
            <Controller
              control={formMethods.control}
              name="instance"
              rules={{ required: 'Instance is required' }}
              render={({ field, fieldState, formState }) => (
                <div className="mb3" data-test-element="instance-container">
                  <label>Instance</label>
                  <div className="flex mb2" style={{ gap: '8px' }}>
                    {INSTANCE_SUGGESTIONS.map((suggestion) => (
                      <Button
                        key={suggestion}
                        plain
                        className="button__small"
                        testId={`instance-chip-${suggestion.toLowerCase()}`}
                        onClick={() => {
                          field.onChange(suggestion);
                          field.onBlur();
                        }}
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                  <input
                    {...field}
                    type="text"
                    className={cn({ ui__error: fieldState.invalid })}
                    data-test-element="instance-input"
                  />
                  <ErrorMessage
                    name="instance"
                    errors={formState.errors}
                    render={({ message }) => (
                      <p className="f6 red db" data-test-element="instance-error">
                        {message}
                      </p>
                    )}
                  />
                </div>
              )}
            />
            {_isNotEmpty(details?.endpoints) && (
              <AdvancedConfig
                endpoints={details.endpoints}
                initialInstance={details?.instance ?? null}
                control={formMethods.control}
                setValue={formMethods.setValue}
                getValues={formMethods.getValues}
              />
            )}
            <div className="mb3" data-test-element="submit-container">
              <Button
                submit
                testId="submit-button"
                disabled={isSubmitting}
                loading={isSubmitting}
                loadingText={isUpdate ? 'Saving' : 'Adding'}
              >
                {isUpdate ? 'Save' : 'Add'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

interface AdvancedConfigProps {
  endpoints: Array<TickatlabEndpointV1>;
  initialInstance: string | null;
  control: ReturnType<typeof useForm<TickatlabFormValues>>['control'];
  setValue: ReturnType<typeof useForm<TickatlabFormValues>>['setValue'];
  getValues: ReturnType<typeof useForm<TickatlabFormValues>>['getValues'];
}

/**
 * Expandable panel showing all tick@lab endpoints with their fully resolved URLs.
 * Each endpoint can be individually customized or reset to the default path.
 */
const AdvancedConfig: FC<AdvancedConfigProps> = ({ endpoints, initialInstance, control, setValue, getValues }) => {
  const [expanded, setExpanded] = useState(false);
  const watchedUrl = useWatch({ control, name: 'url' });
  const watchedInstance = useWatch({ control, name: 'instance' });
  const watchedOverrides = useWatch({ control, name: 'endpoint_overrides' });

  let baseUrlHost = '';
  try {
    baseUrlHost = `https://${new URL(watchedUrl).host}`;
  } catch {
    // invalid URL, leave empty
  }

  return (
    <div className="mb3" data-test-element="advanced-config">
      <div className="flex items-center mb2">
        <Switch value={expanded} onChange={setExpanded} testId="advanced-config-toggle" />
        <span className="ml2 f6 fw5">Advanced Config</span>
      </div>
      {expanded && (
        <div className="ba b--light-silver br2 pa3 bg-near-white" data-test-element="endpoint-panel">
          {endpoints.map((endpoint, index) => {
            const isCustom = _notNil(watchedOverrides?.[endpoint.type]);
            const instanceSuffix = `_${watchedInstance ?? ''}`;
            const basePath = _notNil(initialInstance)
              ? endpoint.path.replace(`_${initialInstance}`, '')
              : endpoint.path;
            const defaultPath = `${basePath}${instanceSuffix}`;
            const resolvedPath = isCustom ? watchedOverrides[endpoint.type] : defaultPath;
            const resolvedUrl = _notNil(baseUrlHost) ? `${baseUrlHost}${resolvedPath}` : resolvedPath;
            const isLast = index === endpoints.length - 1;

            return (
              <EndpointRow
                key={endpoint.type}
                endpoint={endpoint}
                isCustom={isCustom}
                resolvedUrl={resolvedUrl}
                defaultPath={defaultPath}
                setValue={setValue}
                getValues={getValues}
                isLast={isLast}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

interface EndpointRowProps {
  endpoint: TickatlabEndpointV1;
  isCustom: boolean;
  resolvedUrl: string;
  defaultPath: string;
  setValue: AdvancedConfigProps['setValue'];
  getValues: AdvancedConfigProps['getValues'];
  isLast: boolean;
}

const EndpointRow: FC<EndpointRowProps> = ({
  endpoint,
  isCustom,
  resolvedUrl,
  defaultPath,
  setValue,
  getValues,
  isLast,
}) => {
  const [editing, setEditing] = useState(false);

  const handleCustomize = () => {
    const current = getValues('endpoint_overrides') ?? {};
    setValue(
      'endpoint_overrides',
      { ...current, [endpoint.type]: defaultPath },
      {
        shouldValidate: true,
      }
    );
    setEditing(true);
  };

  const handleReset = () => {
    const current = getValues('endpoint_overrides') ?? {};
    const { [endpoint.type]: _, ...rest } = current;
    setValue('endpoint_overrides', rest, { shouldValidate: true });
    setEditing(false);
  };

  const handlePathChange = (newPath: string) => {
    const current = getValues('endpoint_overrides') ?? {};
    setValue(
      'endpoint_overrides',
      { ...current, [endpoint.type]: newPath },
      {
        shouldValidate: true,
      }
    );
  };

  return (
    <div className={`pv2${isLast ? '' : ' bb b--light-silver'}`} data-test-element={`endpoint-row-${endpoint.type}`}>
      <div className="flex items-center justify-between mb1">
        <div className="flex items-center">
          <span className="f6 fw5">{endpoint.label}</span>
          {isCustom && <Chip title="Custom" colour="gray" className="ml2" />}
        </div>
        <div className="flex items-center">
          {isCustom ? (
            <button
              type="button"
              className="link-button f7"
              data-test-element={`reset-${endpoint.type}`}
              onClick={handleReset}
            >
              Reset to default
            </button>
          ) : (
            <button
              type="button"
              className="link-button f7"
              data-test-element={`customize-${endpoint.type}`}
              onClick={handleCustomize}
            >
              Customize
            </button>
          )}
        </div>
      </div>
      {editing && isCustom ? (
        <input
          ref={(el) => {
            if (el) {
              el.scrollLeft = el.scrollWidth;
            }
          }}
          type="text"
          className="w-100 mb1"
          value={getValues(`endpoint_overrides.${endpoint.type}`) ?? ''}
          onChange={(e) => handlePathChange(e.target.value)}
          onFocus={(e) => {
            const len = e.currentTarget.value.length;
            e.currentTarget.setSelectionRange(len, len);
          }}
          onBlur={(e) => {
            e.currentTarget.scrollLeft = e.currentTarget.scrollWidth;
          }}
          data-test-element={`custom-path-${endpoint.type}`}
        />
      ) : null}
      <p
        className="f7 mid-gray ma0"
        style={{ wordBreak: 'break-all' }}
        data-test-element={`url-preview-${endpoint.type}`}
      >
        {resolvedUrl}
      </p>
    </div>
  );
};

const buildExistingOverrides = (details: TeamTickatlabDetailsV1 | null): Record<string, string> => {
  if (_isNil(details?.endpoints)) {
    return {};
  }
  return details.endpoints.reduce<Record<string, string>>((acc, ep) => {
    if (ep.custom) {
      acc[ep.type] = ep.path;
    }
    return acc;
  }, {});
};
